import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import BottomNav from '../../components/feature/BottomNav';
import { referralTiers as defaultTiers, referralTiers } from '../../mocks/referrals';
import { fetchReferrals, fetchReferralTiers, fetchUserInfo, fetchWalletInfo } from '../../api/apiServices';
import { Loader } from 'lucide-react';

const ReferralPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('referral');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refBalance, setRefBalance] = useState(0);
  const [userReferralCode, setUserReferralCode] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [tiers, setTiers] = useState(defaultTiers);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    activeAgents: 0,
    totalReferralEarnings: 0,
    weeklyBonus: 0,
  });

  // Fetch from backend
  // useEffect(() => {
  //   const loadReferrals = async () => {
  //     try {
  //       setLoading(true);
  //       const referralRes = await fetchReferrals();
  //       const tierRes = await fetchReferralTiers();

  //       // Extract useful data from response
  //       setReferrals(referralRes.referrals || []);
  //       setUserReferralCode(referralRes.referralCode || 'N/A');
  //       setReferralStats({
  //         totalReferrals: referralRes.referrals?.length || 0,
  //         activeAgents: referralRes.referrals?.filter(r => r.status === 'active').length || 0,
  //         totalReferralEarnings: referralRes.totalEarnings || 0,
  //         weeklyBonus: referralRes.weeklyBonus || 0,
  //       });

  //       if (tierRes?.tiers?.length) setTiers(tierRes.tiers);
  //     } catch (err) {
  //       console.error('Error fetching referral data:', err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   loadReferrals();
  // }, []);

  useEffect(() => {
  const loadReferralData = async () => {
    try {
      setLoading(true);

      const [userRes, referralRes, tierRes, walletRes] = await Promise.all([
        fetchUserInfo(),
        fetchReferrals(),
        fetchReferralTiers(),
        fetchWalletInfo(),
      ]);

      // ✅ Prefer referral code from user data, fallback to referral data
      const refCode = userRes?.user?.referralCode || referralRes?.referralCode || "N/A";
      setUserReferralCode(refCode);

      // ✅ Wallet
      setRefBalance(walletRes?.data?.referralBalance || 0);

      // ✅ Referrals and stats
      setReferrals(referralRes.referrals || []);
      setReferralStats({
        totalReferrals: referralRes.referrals?.length || 0,
        activeAgents: referralRes.referrals?.filter(r => r.status === "active").length || 0,
        totalReferralEarnings: referralRes.totalEarnings || 0,
        weeklyBonus: referralRes.weeklyBonus || 0,
      });

      // ✅ Tiers
      if (tierRes?.tiers?.length) setTiers(tierRes.tiers);
    } catch (err) {
      console.error("Error loading referral data:", err);
    } finally {
      setLoading(false);
    }
  };

  loadReferralData();
}, []);

  
    // Fetch Wallet Data
    const fetchWalletData = async () => {
      try {
        const res = await fetchWalletInfo();
  
        if (res?.status && res?.data) {
          const refBalance = res.data?.referralBalance || 0;
  
          setRefBalance(refBalance);
        } else {
          console.error("Unexpected wallet data format:", res);
        }
      } catch (error) {
        console.error("Failed to fetch wallet data:", error);
      }
    };
  
    useEffect(() => {
      fetchWalletData();
    }, []);

  // Derived data
  const { totalReferrals, activeAgents, totalReferralEarnings, weeklyBonus } = referralStats;

  const getCurrentTier = () => {
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (activeAgents >= tiers[i].agentsRequired) {
        return tiers[i];
      }
    }
    return null;
  };

  const getNextTier = () => {
    const currentTier = getCurrentTier();
    if (!currentTier) return tiers[0];
    const currentIndex = tiers.findIndex(tier => tier.id === currentTier.id);
    return currentIndex < tiers.length - 1 ? tiers[currentIndex + 1] : null;
  };

  const currentTier = getCurrentTier();
  const nextTier = getNextTier();


  const inviteLink = `https://dulcecomercio.com/auth/register?ref=${userReferralCode}`;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };



  const handleShare = async () => {
    const shareData = {
      title: t('referralProgram'),
      text: `${t('shareMessage')} ${userReferralCode}`,
      url: inviteLink,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        setShowShareModal(true);
      }
    } else {
      setShowShareModal(true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  const renderReferralTab = () => (
    <div className="pt-20 pb-24 px-4">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Agent-recruit rewards 
</h2>
        <p className="text-gray-600">Invite Friends</p>
      </div>

      {/* Referral Code Card */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white mb-6">
          <h3 className="text-lg font-semibold mb-2">Recruit code/ link</h3>
          <div className="bg-white/20 rounded-xl p-4 mb-4">
            <p className="text-2xl font-bold tracking-wider">{userReferralCode}</p>
          </div>
          <div className="flex space-x-3">
            <button onClick={handleCopyCode} className="flex-1 bg-white/20 hover:bg-white/30 rounded-xl py-3 px-4 font-medium transition-colors">
              <i className="ri-file-copy-line mr-2"></i>
              {copySuccess ? t('codeCopied') : t('copyCode')}
            </button>
            <button onClick={handleShare} className="flex-1 bg-white text-orange-500 hover:bg-gray-100 rounded-xl py-3 px-4 font-medium transition-colors">
              <i className="ri-share-line mr-2"></i>
              {t('shareLink')}
            </button>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-group-line text-blue-600"></i>
            </div>
            <p className="text-2xl font-bold text-gray-800">{totalReferrals}</p>
            <p className="text-sm text-gray-600">{t('totalReferrals')}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-user-star-line text-green-600"></i>
            </div>
            <p className="text-2xl font-bold text-gray-800">{activeAgents}</p>
            <p className="text-sm text-gray-600">{t('activeAgents')}</p>
          </div>
        </div>

      {/* Earnings Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">{t('totalEarnings')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('referralEarnings')}:</span>
              <span className="font-semibold text-green-600">${totalReferralEarnings.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('weeklyBonus')}:</span>
              <span className="font-semibold text-blue-600">${weeklyBonus.toFixed(2)}</span>
            </div>
            <div className="border-t pt-3 flex justify-between">
              <span className="font-semibold text-gray-800">{t('total')}:</span>
              <span className="text-xl font-bold text-gray-800">${(totalReferralEarnings + weeklyBonus).toFixed(2)}</span>
            </div>
          </div>
        </div>

      {/* Current Tier */}
      {currentTier && (
        <div className={`bg-gradient-to-r ${currentTier.color} rounded-xl p-6 text-white mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">{t('currentTier')}</p>
              <h3 className="text-xl font-bold">{t(currentTier.nameKey)}</h3>
              <p className="text-white/90 text-sm mt-1">{t(currentTier.descriptionKey)}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className={`${currentTier.icon} text-white text-xl`}></i>
            </div>
          </div>
        </div>
      )}

      {/* Progress to Next Tier */}
      {nextTier && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">{t('nextTier')}: {t(nextTier.nameKey)}</h3>
            <span className="text-sm text-gray-600">
              {activeAgents}/{nextTier.agentsRequired}
            </span>
          </div>
          <div className="bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-gradient-to-r from-orange-500 to-red-500 rounded-full h-3 transition-all duration-300"
              style={{ width: `${Math.min((activeAgents / nextTier.agentsRequired) * 100, 100)}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {nextTier.agentsRequired - activeAgents} {t('agentsNeeded')}
          </p>
        </div>
      )}

      {/* Reward Tiers */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-gray-800 mb-4">{t('rewardTiers')}</h3>
        <div className="space-y-4">
          {referralTiers.map((tier) => (
            <div
              key={tier.id}
              className={`p-4 rounded-lg border-2 ${
                currentTier?.id === tier.id
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 bg-gradient-to-r ${tier.color} rounded-full flex items-center justify-center`}>
                    <i className={`${tier.icon} text-white`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{t(tier.nameKey)}</h4>
                    <p className="text-sm text-gray-600">{tier.agentsRequired} {t('activeAgents')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">${tier.reward}</p>
                  {tier.weeklyBonus > 0 && (
                    <p className="text-xs text-blue-600">+${tier.weeklyBonus} {t('weekly')}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Referrals */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">{t('recentReferrals')}</h3>
        <div className="space-y-3">
          {referrals.slice(0, 5).map((referral) => (
            <div key={referral.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {referral.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">{referral.name}</p>
                  <p className="text-sm text-gray-600">{t('joinedOn')} {referral.joinDate}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                  referral.status === 'active'
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {t(referral.status)}
                </span>
                <p className="text-sm text-gray-600 mt-1">${referral.earnings}</p>
              </div>
            </div>
          ))}
          {referrals.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-gray-500 mb-2">{t('noReferrals')}</p>
              <p className="text-sm text-gray-400">{t('startInviting')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="relative">
        {renderReferralTab()}
      </main>

      <BottomNav activeTab={activeTab} onTabChange={(tab) => {
        if (tab === 'referral') {
          setActiveTab(tab);
        } else {
          navigate('/');
        }
      }} />

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-share-line text-orange-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">{t('shareReferralLink')}</h3>
            <p className="text-gray-600 mb-4">{t('shareMessage')}</p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4 break-all">
              {window.location.origin}/auth/register?ref={userReferralCode}
            </div>
            <div className="flex space-x-3">
              <button onClick={() => setShowShareModal(false)} className="flex-1 py-3 border rounded-xl font-medium text-gray-700 hover:bg-gray-50">
                {t('close')}
              </button>
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(`${window.location.origin}/auth/register?ref=${userReferralCode}`);
                  setShowShareModal(false);
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }}
                className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600"
              >
                {t('copyLink')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralPage;